<template>
  <div class="payment-links-table">
    <!-- Selection Summary -->
    <div v-if="selectionEnabled && selectedLinks.length > 0" class="selection-summary">
      <div class="summary-item">
        <span class="summary-label">{{ t('Selecionados') }}:</span>
        <span class="summary-value">{{ selectedLinks.length }}</span>
      </div>
      <div class="summary-divider"></div>
      <div class="summary-item">
        <span class="summary-label">{{ t('Total Selecionado') }}:</span>
        <span class="summary-value">{{ n(selectedTotal, 'currency') }}</span>
      </div>
      <div class="summary-divider"></div>
      <div class="summary-actions">
        <Button
          data-testid="payment-links-batch-sync-button"
          @click="handleBatchSync"
          :disabled="!canBatchSync"
          severity="secondary"
          size="small"
          :label="t('Sincronizar Selecionados')"
          icon="pi pi-sync"
        />
        <Button
          data-testid="payment-links-clear-selection-button"
          @click="clearSelection"
          severity="secondary"
          size="small"
          :label="t('Limpar Seleção')"
          icon="pi pi-times"
          text
        />
      </div>
    </div>

    <DataTable
      ref="dataTableRef"
      v-model:selection="selectedLinks"
      :key="tableKey"
      display-mode="handle_user_device"
      :config="tableConfigComputed"
      :data="enrichedLinks"
      :filterConfig="filterConfig"
      :auto-load="false"
      :refreshButton="{ show: true }"
      @refresh="$emit('refresh')"
      @row-click="handleRowClick"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick"
      @selection-change="handleSelectionChange" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from '../../grid/DataTable.vue';
import { log } from '../../utils/DebugLogger';
import { eventBus } from '../../eventBus';
import { usePaymentLinks } from '../../composables/usePaymentLinks';

type Id = number | string;

interface PaymentLinkRow {
  id: Id;
  status?: string;
  url?: string;
  valor?: number | string;
  owner?: string;
  owner_id?: number | string;
  provider_link_id?: string;
  [key: string]: unknown;
}

interface EnrichedPaymentLink extends PaymentLinkRow {
  owner_display: string;
}

interface CellParams {
  value: unknown;
  data?: PaymentLinkRow;
}

interface ButtonClickPayload {
  value: Id;
  data: PaymentLinkRow;
  action: string;
}

interface DataTableInstance {
  clearSelection?: () => void;
}

interface RowClickEvent {
  data?: PaymentLinkRow;
}

const props = withDefaults(
  defineProps<{
    links?: PaymentLinkRow[];
    loading?: boolean;
    selectionEnabled?: boolean;
  }>(),
  {
    links: () => [],
    loading: false,
    selectionEnabled: true,
  }
);

const emit = defineEmits<{
  (e: 'refresh'): void;
  (e: 'view-link', id: Id): void;
  (e: 'view-owner', payload: { owner: string; ownerId: Id; rowData: PaymentLinkRow }): void;
  (e: 'copy-link', url: string): void;
  (e: 'cancel-link', id: Id): void;
  (e: 'sync-link', id: Id): void;
  (e: 'batch-sync', ids: Id[]): void;
  (e: 'selection-change', selection: PaymentLinkRow[]): void;
}>();

const { t, n } = useI18n();
const { getLinkStatusConfig } = usePaymentLinks();

// Refs
const dataTableRef = ref<DataTableInstance | null>(null);
const tableKey = ref<number>(0);
const selectedLinks = ref<PaymentLinkRow[]>([]);

// Computed: Selected total value
const selectedTotal = computed<number>(() => {
  return selectedLinks.value.reduce((sum, link) => {
    return sum + (parseFloat(String(link.valor)) || 0);
  }, 0);
});

// Computed: Can batch sync (only links with provider_link_id and not in terminal state)
const canBatchSync = computed<boolean>(() => {
  if (selectedLinks.value.length === 0) return false;

  const terminalStatuses = ['cancelled', 'refunded'];
  return selectedLinks.value.some(link =>
    !!link.provider_link_id && !terminalStatuses.includes(link.status || '')
  );
});

// Computed: Links that can be synced from selection
const syncableLinks = computed<PaymentLinkRow[]>(() => {
  const terminalStatuses = ['cancelled', 'refunded'];
  return selectedLinks.value.filter(link =>
    !!link.provider_link_id && !terminalStatuses.includes(link.status || '')
  );
});

// Watch for links changes to clear selection
watch(() => props.links, () => {
  // Clear selection when data changes (e.g., after refresh)
  selectedLinks.value = [];
}, { deep: true });

// Owner type labels for display
const getOwnerLabel = (owner?: string): string => {
  const ownerLabels: Record<string, string> = {
    'compromisso': 'Compromisso',
    'orcamento': 'Orçamento',
    'recebimento': 'Recebimento'
  };
  return ownerLabels[owner || ''] || owner || '-';
};

// Status severity mapping for badges
const getStatusSeverityClass = (status?: string): string => {
  const severityMap: Record<string, string> = {
    'active': 'info',
    'paid': 'success',
    'partially_paid': 'warn',
    'expired': 'danger',
    'overdue': 'danger',
    'cancelled': 'secondary',
    'refunded': 'warn'
  };
  return severityMap[status || ''] || 'secondary';
};

// Computed: Enriched links data
const enrichedLinks = computed<EnrichedPaymentLink[]>(() => {
  return props.links.map(link => {
    // Build owner display string
    const ownerDisplay = link.owner && link.owner_id
      ? `${getOwnerLabel(link.owner)} #${link.owner_id}`
      : '-';

    return {
      ...link,
      owner_display: ownerDisplay
    };
  });
});

// Render functions
const renderStatus = (params: CellParams): HTMLElement => {
  const status = params.value as string | undefined;
  const config = getLinkStatusConfig(status) as { icon: string; label: string };
  const severityClass = getStatusSeverityClass(status);

  // Create a styled badge similar to PrimeVue Tag
  const span = document.createElement('span');
  span.className = `status-badge status-${severityClass}`;
  span.innerHTML = `<i class="pi ${config.icon}"></i> ${t(config.label)}`;
  return span;
};

// Handle owner click - opens appropriate modal
const handleOwnerClick = (_ownerDisplay: string, rowData: PaymentLinkRow): void => {
  const owner = rowData.owner;
  const ownerId = rowData.owner_id;

  log('PaymentLinksTable', 'handleOwnerClick', 'Owner clicked', { owner, ownerId, rowData });

  if (!owner || !ownerId) {
    log('PaymentLinksTable', 'handleOwnerClick', 'No owner or owner_id');
    return;
  }

  const modalEvents: Record<string, string> = {
    'compromisso': 'modal.gestao-compromisso.open',
    'orcamento': 'modal.orcamento.open',
    'recebimento': 'modal.recebimento.open'
  };

  const eventName = modalEvents[owner];
  if (eventName) {
    log('PaymentLinksTable', 'handleOwnerClick', 'Emitting event', eventName);
    eventBus.emit(eventName, { id: parseInt(String(ownerId)) });
  } else {
    log('PaymentLinksTable', 'handleOwnerClick', 'Unknown owner type', owner);
  }

  emit('view-owner', { owner, ownerId, rowData });
};

// Table configuration
const tableConfigComputed = computed(() => ({
  tableId: 'paymentLinksDataTable',
  rowTestId: (data: Record<string, unknown>) => {
    const id = data?.id;
    return id == null ? '' : `payment-links-row-${id}`;
  },
  checkbox: props.selectionEnabled ? {
    enabled: true,
    position: 'first',
    width: '3rem'
  } : { enabled: false },
  columns: [
    {
      data: 'id',
      title: t('ID'),
      footerType: 'count',
      renderType: 'clickable_id'
    },
    {
      data: 'franquia',
      title: t('Franquia')
    },
    {
      data: 'status',
      title: t('Status'),
      cellRenderer: renderStatus
    },
    {
      data: 'owner_display',
      title: t('Origem'),
      renderType: 'custom_clickable_id',
      clickCallback: handleOwnerClick
    },
    {
      data: 'description',
      title: t('Descricao')
    },
    {
      data: 'valor',
      title: t('Valor'),
      renderType: 'currency',
      footerType: 'sum'
    },
    {
      data: 'payment_method_label',
      title: t('Forma de Pagamento')
    },
    {
      data: 'due_date',
      title: t('Vencimento'),
      renderType: 'date',
      dataType: 'date'
    },
    {
      data: 'created_at',
      title: t('Criado em'),
      renderType: 'datetime',
      dataType: 'date'
    },
    {
      data: 'url',
      title: t('Link'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Copiar'),
      buttonIcon: 'pi pi-copy',
      buttonClass: 'p-button-secondary p-button-sm',
      buttonAction: 'copy_link',
      buttonCondition: (row: Record<string, unknown>) => !!(row as PaymentLinkRow).url
    },
    {
      data: 'id',
      title: t('Sincronizar'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Sincronizar'),
      buttonIcon: 'pi pi-sync',
      buttonClass: 'p-button-secondary p-button-sm',
      buttonAction: 'sync',
      buttonCondition: (row: Record<string, unknown>) => {
        const rowData = row as PaymentLinkRow;
        // Can sync if has provider_link_id and not in terminal state
        if (!rowData.provider_link_id) return false;
        const terminalStatuses = ['cancelled', 'refunded'];
        return !terminalStatuses.includes(rowData.status || '');
      }
    },
    {
      data: 'id',
      title: t('Cancelar'),
      sortable: false,
      renderType: 'clickable_button',
      buttonText: t('Cancelar'),
      buttonIcon: 'pi pi-times',
      buttonClass: 'p-button-danger p-button-sm',
      buttonAction: 'cancel',
      buttonCondition: (row: Record<string, unknown>) => {
        const rowData = row as PaymentLinkRow;
        return rowData.status === 'active' || rowData.status === 'overdue';
      }
    }
  ],
  pagination: true,
  pageLength: 25,
  filterDisplay: 'row',
  heightMode: 'fill-viewport',
  footerLabel: t('Totais')
}));

// Filter configuration
const filterConfig = computed(() => ({
  columns: {
    'id': 'text',
    'franquia': 'select',
    'status': 'select',
    'owner': 'select',
    'description': 'text',
    'valor': 'number',
    'payment_method_label': 'select',
    'due_date': 'date',
    'created_at': 'date'
  }
}));

// Methods
const handleRowClick = (event: RowClickEvent): void => {
  log('PaymentLinksTable', 'handleRowClick', 'Row clicked', event?.data?.id || null);
};

const handleEditClick = (linkId: Id): void => {
  log('PaymentLinksTable', 'handleEditClick', 'Viewing link', linkId);
  emit('view-link', linkId);
};

const handleButtonClick = ({ value, data, action }: ButtonClickPayload): void => {
  log('PaymentLinksTable', 'handleButtonClick', 'Button clicked', { value, action });

  switch (action) {
    case 'copy_link':
      emit('copy-link', String(data.url || ''));
      break;
    case 'cancel':
      emit('cancel-link', value);
      break;
    case 'sync':
      emit('sync-link', value);
      break;
    default:
      log('PaymentLinksTable', 'handleButtonClick', 'Unknown button action', action);
  }
};

// Selection handlers
const handleSelectionChange = (payload: { selection: any[]; added: any[]; removed: any[]; }): void => {
  const selection = payload?.selection || [];
  log('PaymentLinksTable', 'handleSelectionChange', 'Selection changed', { count: selection.length });
  emit('selection-change', selection as PaymentLinkRow[]);
};

const handleBatchSync = (): void => {
  const linksToSync = syncableLinks.value;
  log('PaymentLinksTable', 'handleBatchSync', 'Batch sync requested', {
    selectedCount: selectedLinks.value.length,
    syncableCount: linksToSync.length
  });

  if (linksToSync.length === 0) {
    eventBus.emit('globalMessage.warning', t('Nenhum link selecionado pode ser sincronizado'));
    return;
  }

  // Emit the syncable link IDs
  const linkIds = linksToSync.map(link => link.id);
  emit('batch-sync', linkIds);
};

const clearSelection = (): void => {
  log('PaymentLinksTable', 'clearSelection', 'Clearing selection');
  selectedLinks.value = [];
};

// Expose methods for parent components
const forceRefresh = (): void => {
  tableKey.value += 1;
};

const getSelectedLinks = (): PaymentLinkRow[] => {
  return selectedLinks.value;
};

const setSelection = (links: PaymentLinkRow[]): void => {
  selectedLinks.value = links;
};

defineExpose({
  forceRefresh,
  getSelectedLinks,
  setSelection,
  clearSelection
});
</script>

<style scoped>
.payment-links-table {
  width: 100%;
}

/* Selection Summary */
.selection-summary {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 1.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  background: var(--p-blue-50, var(--surface-50));
  border-radius: 8px;
  border: 1px solid var(--p-blue-200, var(--surface-border));
}

.summary-item {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
}

.summary-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  font-weight: 500;
}

.summary-value {
  font-size: 1rem;
  font-weight: 700;
  color: var(--p-blue-700, var(--text-color));
}

.summary-divider {
  width: 1px;
  height: 24px;
  background: var(--p-blue-200, var(--surface-border));
}

.summary-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

/* Status badge styles */
:deep(.status-badge) {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

:deep(.status-badge i) {
  font-size: 0.7rem;
}

:deep(.status-info) {
  background-color: var(--p-blue-100);
  color: var(--p-blue-700);
}

:deep(.status-success) {
  background-color: var(--p-green-100);
  color: var(--p-green-700);
}

:deep(.status-warn) {
  background-color: var(--p-yellow-100);
  color: var(--p-yellow-700);
}

:deep(.status-danger) {
  background-color: var(--p-red-100);
  color: var(--p-red-700);
}

:deep(.status-secondary) {
  background-color: var(--p-surface-200);
  color: var(--p-surface-600);
}
</style>
